import { TourAnchor, anchorSelector } from './tourAnchors';
import { PANEL_STILL_TIMEOUT_MS } from './tourTiming';

// Pages fetch before they render, so an anchor can appear seconds after the
// route mounts. Poll on a timer AND watch the DOM, so a late render is caught
// immediately rather than after the next tick — and give up eventually instead
// of watching forever.
const DEFAULT_TIMEOUT_MS = 20000;
const POLL_MS = 200;

export interface AnchorWait {
  cancel: () => void;
}

export const waitForAnchor = (anchor: TourAnchor, onFound: (el: HTMLElement) => void, timeoutMs = DEFAULT_TIMEOUT_MS): AnchorWait => {
  const selector = anchorSelector(anchor);
  let done = false;
  let observer: MutationObserver | null = null;
  let poll: number | undefined;
  let deadline: number | undefined;

  const finish = (el: HTMLElement) => {
    if (done) return;
    done = true;
    observer?.disconnect();
    window.clearInterval(poll);
    window.clearTimeout(deadline);
    onFound(el);
  };

  // Drawers are keepMounted, so an anchor can exist long before the user opens
  // the panel it lives in. A closed MUI modal keeps its subtree in the DOM at
  // full size — hidden only via `visibility` and `aria-hidden` — so measuring
  // the box alone reports it as visible and the walkthrough fires early.
  const isVisible = (el: HTMLElement) => {
    const cs = getComputedStyle(el);
    // `visibility` inherits, so this also catches a hidden ancestor.
    if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') return false;
    if (el.closest('[aria-hidden="true"]')) return false;
    if (!el.offsetParent && cs.position !== 'fixed') return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };

  const check = () => {
    // Scan every match, not just the first: an anchor can appear on more than
    // one element (table rows) or on a hidden one that renders before the real
    // target. Taking querySelector's first hit would stall on an invisible node.
    const found = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(isVisible);
    if (found) finish(found);
  };

  check();
  if (!done) {
    observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    // Belt and braces: a node already present but only just made visible does
    // not always trigger a mutation record.
    poll = window.setInterval(check, POLL_MS);
    deadline = window.setTimeout(() => {
      done = true;
      observer?.disconnect();
      window.clearInterval(poll);
    }, timeoutMs);
  }

  return {
    cancel: () => {
      done = true;
      observer?.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(deadline);
    }
  };
};

// A drawer reads visible on the first frame of its slide-in; anything placed
// against its anchor before the box settles ends up under the panel.
const STILL_FRAMES = 6;

/** Resolves once the element's box has held still for a few frames (or the cap). */
export const waitForStill = (el: Element, isCancelled: () => boolean = () => false): Promise<void> =>
  new Promise((resolve) => {
    const deadline = performance.now() + PANEL_STILL_TIMEOUT_MS;
    let last = '';
    let stillFor = 0;
    const tick = () => {
      if (isCancelled()) return;
      const r = el.getBoundingClientRect();
      const key = `${r.left},${r.top},${r.width},${r.height}`;
      stillFor = key === last ? stillFor + 1 : 0;
      last = key;
      if (stillFor >= STILL_FRAMES || performance.now() > deadline) resolve();
      else window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });
